import CloseIcon from '@mui/icons-material/Close';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,Stack,TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../../helpers/attToken';
import { trackEventMatomo } from '../../../helpers/matomo';
import queryClient from '../../../service/query';
import ServiceTypes from '../../../service/ServiceTypes';
import { service } from '../../../types/services/types';

const serviceType = new ServiceTypes();

export default function ModalServiceType({
  open,
  setOpen,
  typeData,
  setTypeData,
}:
  {
    open: boolean,
    setOpen: Function,
    typeData: service,
    setTypeData: Function,
  }) {

  const { t } = useTranslation('translation');

  const { mutate, isPending } = useMutation({
    mutationKey: [typeData?.tipoId ? 'EDIT' : 'CREATE'],
    mutationFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');
      const createBody = {
        ...token,
        empresa: token.userEmpresa,
        tipoNome: typeData?.nome,
        descricao: typeData?.descricao,
      };
      const editBody = {
        ...token,
        empresa: token.userEmpresa,
        tipo: {
          nome: typeData?.nome,
          tipoId: typeData?.tipoId,
          dataCriacao: typeData?.dataCriacao,
          descricao: typeData?.descricao,
        },
      };
      try {
        const promise = toast.promise(async () => {
          if (typeData?.tipoId) {
            const response = await serviceType.editServiceType(editBody);
            if (!response) throw new Error('Error editing service type');
            return response;

          } else {
            const response = await serviceType.createServiceType(createBody);
            if (!response) throw new Error('Error creating service type');
            return response;
          }
        }, {
          pending: typeData?.tipoId ? `${t('Services.ServiceModal.savingChange')}` : `${t('Services.ServiceModal.creatingNewServiceType')}`,
          success: typeData?.tipoId ? `${t('Services.servicesManagement.changeName')}` : `${t('Services.ServiceModal.newServiceCreated')}`,
          error: typeData?.tipoId ? `${t('Services.ServiceModal.errorSavingChange')}` : `${t('Services.ServiceModal.errorCreatingNewService')}`,
        }, { toastId: 'serviceTypeModal' });
        return promise;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    onSuccess: () => {
      setTypeData(null);
      setOpen(false);
      queryClient.resetQueries({ queryKey: ['GET_SERVICES'] });
      trackEventMatomo('gestão de serviços', 'sucesso', typeData?.tipoId ? 'edição' : 'criação', typeData?.tipoId ? 'tipo de serviço editado' : 'tipo de serviço criado');
    },
    onError: (error) => {
      trackEventMatomo('gestão de serviços', 'erro', typeData?.tipoId ? 'edição' : 'criação', typeData?.tipoId ? 'falha ao editar tipo de serviço' : 'falha ao criar tipo de serviço');
      console.error(error);
    },
  });

  const close = () => {
    setTypeData(null);
    trackEventMatomo('Modal associação tipo de serviço', 'click', 'button', 'cancela');
    setOpen(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={close}
      scroll={'paper'}
    >
      <DialogTitle>{typeData?.tipoId
        ? `${t('Services.servicesManagement.editName')}`
        : `${t('notifications.ticket.criarTipoServico')}`}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={close}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack direction="column" spacing={2} width={'100%'}>
          <TextField
            size='small'
            onClick={() => trackEventMatomo('Modal associação tipo de serviço', 'click', 'textfiel', 'edita valor')}
            style={{ marginBottom: '10px' }}
            label={t('name') + ':'}
            fullWidth
            value={typeData?.nome}
            onChange={(e) => setTypeData({ ...typeData, nome: e.target.value })}
          />
          <TextField
            size='small'
            onClick={() => trackEventMatomo('Modal associação tipo de serviço', 'click', 'textfiel', 'edita valor')}
            multiline
            rows={2}
            maxRows={4}
            label={t('Services.servicesManagement.description')}
            fullWidth
            value={typeData?.descricao}
            onChange={(e) => setTypeData({ ...typeData, descricao: e.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => mutate()} disabled={!typeData?.nome || isPending}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
